import styled, { css } from 'styled-components';

import { MiniBadge } from '@xing-com/badge';
import { Card as BaseCard } from '@xing-com/card';
import { ApplyButton as BaseApplyButton } from '@xing-com/crate-jobs-components-apply-button';
import { SGapCol, XxsGapCol } from '@xing-com/crate-jobs-components-columns';
import { OptimisticPublicationDate } from '@xing-com/crate-jobs-components-optimistic-publication-date';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { NotOnSmall, OnlyOnSmall } from '@xing-com/layout-primitives';
import { mediaConfined, mediaTopBarVisible } from '@xing-com/layout-tokens';
import { Marker as BaseMarker } from '@xing-com/marker';
import { ProfileImage } from '@xing-com/profile-image';
import {
  motionEasingStandard,
  motionTimeS,
  scale320,
  spaceM,
  spaceS,
  spaceXS,
  xdlColorBackgroundSecondary,
  xdlColorText,
  xdlPaletteRed01,
  xdlPaletteRed10,
} from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

const urgentlyNeededStyles = css`
  /* This overrides the hover bg */
  background-color: ${xdlColorBackgroundSecondary};

  && {
    transition: box-shadow ${motionTimeS} ${motionEasingStandard};

    &:hover {
      /* Grows "padding" without affecting layout */
      box-shadow: 0 0 0 6px ${xdlColorBackgroundSecondary};
    }
  }

  @media ${mediaTopBarVisible} {
    margin-inline: -${spaceS};
    padding-inline: ${spaceS};
    border-radius: ${spaceS};
  }
`;

export const Card = styled(BaseCard)<{ $urgentlyNeeded?: boolean }>`
  color: ${xdlColorText};
  border: none;
  padding: 0;

  > div {
    flex-direction: row;
  }

  ${({ $urgentlyNeeded }) => $urgentlyNeeded && urgentlyNeededStyles}

  &:hover,
  &:focus,
  &:focus-within {
    z-index: 1;

    &::after {
      border: none;
    }
  }
`;

export const Image = styled(ProfileImage)`
  pointer-events: none;
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media ${mediaConfined} {
    flex-direction: row;
  }
`;

export const Row = styled.div`
  display: flex;
  width: 100%;
`;

export const BigScreenCTA = styled(NotOnSmall)`
  margin-left: ${spaceM};
`;

export const SmallScreenCTA = styled(OnlyOnSmall)`
  margin-left: ${spaceM};
`;

export const Content = styled(SGapCol)`
  width: 100%;
  margin-left: ${spaceM};
`;

export const Marker = styled(BaseMarker)`
  width: fit-content;
`;

export const UrgentMarker = styled(Marker)`
  background-color: ${xdlPaletteRed01};

  & > span {
    color: ${xdlPaletteRed10};
  }
`;

export const MainInfo = styled(XxsGapCol)`
  margin-left: ${spaceXS};
`;

export const Title = styled(Headline)`
  ${lineClamp(2)};
  word-break: break-word;
`;

export const Company = styled(BodyCopy)`
  ${lineClamp(1)};
  word-break: break-all;
`;

export const City = styled(BodyCopy)`
  ${lineClamp(1)};
  word-break: break-all;
`;

export const PublicationDate = styled(OptimisticPublicationDate).attrs({
  size: 'xsmall',
})`
  display: flex;
  align-items: center;
  margin: 0;
  margin-left: ${spaceXS};
`;

export const Notification = styled(MiniBadge)`
  margin-right: ${spaceXS};
`;

export const ApplyButton = styled(BaseApplyButton)`
  width: 100%;
  min-width: ${scale320};
`;
